import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Lock, ArrowRight } from "lucide-react";
import { RESET_DAYS, RESET_COURSE_DURATION_DAYS } from "@/data/resetCourse";
import { api } from "@/lib/api";
import BookingButton from "@/components/BookingButton";

const SIGNUP_KEY = "sano_reset_signup";

function loadSignup() {
  try { return JSON.parse(localStorage.getItem(SIGNUP_KEY) || "null"); }
  catch { return null; }
}
function saveSignup(o) { localStorage.setItem(SIGNUP_KEY, JSON.stringify(o)); }

function dayFromSignup(ts) {
  const days = Math.floor((Date.now() - ts) / (1000 * 60 * 60 * 24)) + 1;
  return Math.min(Math.max(1, days), RESET_COURSE_DURATION_DAYS);
}

export default function ResetPage() {
  const [signup, setSignup] = useState(loadSignup());
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeDay, setActiveDay] = useState(1);

  useEffect(() => {
    if (signup) setActiveDay(dayFromSignup(signup.ts));
  }, [signup]);

  const enroll = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/reset/signup", { email, name: name || null });
      const obj = { email, name, ts: Date.now() };
      saveSignup(obj);
      setSignup(obj);
      setActiveDay(1);
      toast.success("You're in. Day 1 is unlocked. Bookmark this page to return daily.");
    } catch {
      toast.error("Couldn't sign up just now. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  if (!signup) {
    return (
      <main className="min-h-[85vh]" data-testid="reset-landing">
        <section className="py-20 lg:py-28">
          <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
            <div className="eyebrow mb-4">Free · 7 days · No spam</div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-6">
              The 7-day<br/><em className="italic" style={{ color: "var(--sano-olive)" }}>Nervous System Reset</em>
            </h1>
            <p className="text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: "var(--sano-ink-2)" }}>
              One short practice a day, for seven days. Polyvagal-informed,
              acupressure-supported, designed for over-scheduled bodies. Each day
              takes about five minutes. The whole week takes you somewhere quieter.
            </p>

            <form onSubmit={enroll} className="mt-12 max-w-md mx-auto" data-testid="reset-signup-form">
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <input
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="px-4 py-3 border bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#3A5A40] text-sm"
                  style={{ borderColor: "var(--sano-border)" }}
                  data-testid="reset-name"
                />
                <input
                  type="email"
                  required
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="px-4 py-3 border bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#3A5A40] text-sm"
                  style={{ borderColor: "var(--sano-border)" }}
                  data-testid="reset-email"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !email}
                className="w-full px-7 py-3.5 rounded-full text-sm font-medium text-[#F9F6F0] disabled:opacity-50"
                style={{ background: "var(--sano-olive)" }}
                data-testid="reset-submit"
              >
                {loading ? "Enrolling…" : "Begin the reset"}
              </button>
              <p className="mt-4 text-xs" style={{ color: "var(--sano-ink-2)" }}>
                Free. You&apos;ll bookmark this page and return each morning. One new day unlocks every 24 hours.
              </p>
            </form>

            <div className="mt-16 grid sm:grid-cols-3 gap-px" style={{ background: "var(--sano-border)" }}>
              {[
                { k: "5 min", v: "per day" },
                { k: "7 days", v: "total" },
                { k: "0", v: "calendar invites" },
              ].map((x) => (
                <div key={x.v} className="p-6" style={{ background: "var(--sano-bg)" }}>
                  <div className="font-serif-display text-3xl" style={{ color: "var(--sano-olive)" }}>{x.k}</div>
                  <div className="text-xs uppercase tracking-widest mt-1" style={{ color: "var(--sano-ink-2)" }}>{x.v}</div>
                </div>
              ))}
            </div>

            <div className="mt-16">
              <Link to="/" className="text-sm border-b border-current pb-0.5" data-testid="reset-back-home">
                ← Back to home
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  const day = RESET_DAYS.find((d) => d.n === activeDay) || RESET_DAYS[0];

  return (
    <main className="min-h-[85vh]" data-testid="reset-course">
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="eyebrow mb-4">Welcome back{signup.name ? `, ${signup.name}` : ""}</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl leading-tight mb-3">
            The 7-day Nervous System Reset
          </h1>
          <p className="text-base leading-relaxed mb-10" style={{ color: "var(--sano-ink-2)" }}>
            Day {dayFromSignup(signup.ts)} of {RESET_COURSE_DURATION_DAYS}. One new day unlocks every 24 hours.
          </p>

          {/* Day picker */}
          <div className="flex flex-wrap gap-2 mb-10" data-testid="reset-day-picker">
            {RESET_DAYS.map((d) => {
              const unlocked = d.n <= dayFromSignup(signup.ts);
              const active = d.n === activeDay;
              return (
                <button
                  key={d.n}
                  disabled={!unlocked}
                  onClick={() => setActiveDay(d.n)}
                  className="px-4 py-2 text-xs border rounded-full inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background: active ? "var(--sano-olive)" : "var(--sano-bg)",
                    color: active ? "var(--sano-bg)" : "var(--sano-ink)",
                    borderColor: active ? "var(--sano-olive)" : "var(--sano-border)",
                  }}
                  data-testid={`reset-day-tab-${d.n}`}
                >
                  {unlocked ? <CheckCircle2 size={12} strokeWidth={1.5} /> : <Lock size={12} strokeWidth={1.5} />}
                  Day {d.n}
                </button>
              );
            })}
          </div>

          {/* Day card */}
          <article
            className="p-10 lg:p-14 border"
            style={{ background: "var(--sano-bg-2)", borderColor: "var(--sano-border)" }}
            data-testid={`reset-day-card-${day.n}`}
          >
            <div className="eyebrow mb-3" style={{ color: "var(--sano-olive)" }}>{day.eyebrow}</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-tight mb-4">{day.title}</h2>
            <p className="text-lg leading-relaxed mb-8" style={{ color: "var(--sano-ink-2)" }}>{day.summary}</p>

            <div className="grid sm:grid-cols-3 gap-px mt-8" style={{ background: "var(--sano-border)" }}>
              <div className="p-6 sm:col-span-2" style={{ background: "var(--sano-bg)" }}>
                <div className="eyebrow mb-2">Today&apos;s practice</div>
                <p className="text-base leading-relaxed">{day.practice}</p>
              </div>
              <div className="p-6" style={{ background: "var(--sano-bg)" }}>
                <div className="eyebrow mb-2">Press this</div>
                <div className="font-serif-display text-lg mb-1">{day.point.n}</div>
                <div className="text-xs leading-relaxed" style={{ color: "var(--sano-ink-2)" }}>{day.point.how}</div>
              </div>
            </div>

            <div
              className="mt-8 p-6 border text-center"
              style={{ background: "var(--sano-bg)", borderColor: "var(--sano-border)" }}
            >
              <div className="eyebrow mb-2">Mantra</div>
              <div className="font-serif-display text-2xl italic leading-snug">“{day.mantra}”</div>
            </div>

            <p className="mt-8 text-sm" style={{ color: "var(--sano-ink-2)" }}>{day.closing}</p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <BookingButton testid={`reset-day-book-${day.n}`} label="Book a real session with Joannie" />
              {day.n < RESET_COURSE_DURATION_DAYS && (
                <span className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--sano-ink-2)" }}>
                  Come back tomorrow for Day {day.n + 1} <ArrowRight size={14} strokeWidth={1.5} />
                </span>
              )}
            </div>
          </article>

          <div className="mt-10">
            <Link to="/" className="text-sm border-b border-current pb-0.5" data-testid="reset-home-link">
              ← Back to home
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
